// Frontend wrappers for org-related Cloud Functions and Firestore reads.
// Call these rather than importing httpsCallable directly so the call
// surface is in one place — easier to mock in tests, easier to add retry
// or instrumentation later.

import { httpsCallable } from 'firebase/functions';
import { getDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { functions } from '../firebase';
import {
  orgDoc,
  orgMembersCol,
  orgPendingInvitesCol,
  orgPendingInviteDoc,
  orgMemberDoc,
} from './firestore';
import type { Org, OrgMember, PendingInvite, Role } from './types';

interface CreateOrgRequest {
  name: string;
}
interface CreateOrgResponse {
  orgId: string;
}

/**
 * Create a new org with the caller as its first admin. Server-side it
 * writes orgs/{newOrgId}, orgs/{newOrgId}/members/{uid} (role admin), and
 * users/{uid}.orgId in a single transaction.
 */
export async function createOrg(name: string): Promise<string> {
  const callable = httpsCallable<CreateOrgRequest, CreateOrgResponse>(functions, 'createOrg');
  const result = await callable({ name });
  return result.data.orgId;
}

export async function getOrg(orgId: string): Promise<Org | null> {
  const snap = await getDoc(orgDoc(orgId));
  if (!snap.exists()) return null;
  const data = snap.data() as Omit<Org, 'createdAt'> & {
    createdAt: number | { toMillis(): number };
  };
  return {
    ...data,
    createdAt:
      typeof data.createdAt === 'number' ? data.createdAt : data.createdAt.toMillis(),
  };
}

// ---------- Members ----------

export async function listMembers(orgId: string): Promise<OrgMember[]> {
  const snap = await getDocs(query(orgMembersCol(orgId), orderBy('joinedAt', 'asc')));
  return snap.docs.map((d) => {
    const raw = d.data() as Omit<OrgMember, 'joinedAt'> & {
      joinedAt: number | { toMillis(): number };
    };
    return {
      ...raw,
      joinedAt:
        typeof raw.joinedAt === 'number' ? raw.joinedAt : raw.joinedAt.toMillis(),
    };
  });
}

export async function getMember(orgId: string, userId: string): Promise<OrgMember | null> {
  const snap = await getDoc(orgMemberDoc(orgId, userId));
  if (!snap.exists()) return null;
  const raw = snap.data() as Omit<OrgMember, 'joinedAt'> & {
    joinedAt: number | { toMillis(): number };
  };
  return {
    ...raw,
    joinedAt:
      typeof raw.joinedAt === 'number' ? raw.joinedAt : raw.joinedAt.toMillis(),
  };
}

export async function setMemberRole(orgId: string, userId: string, role: Role): Promise<void> {
  const callable = httpsCallable(functions, 'setMemberRole');
  await callable({ orgId, userId, role });
}

export async function removeMember(orgId: string, userId: string): Promise<void> {
  const callable = httpsCallable(functions, 'removeMember');
  await callable({ orgId, userId });
}

// ---------- Invites ----------

export async function inviteMember(
  orgId: string,
  email: string,
  role: Role,
): Promise<string> {
  const callable = httpsCallable<unknown, { inviteId: string }>(functions, 'inviteMember');
  const result = await callable({ orgId, email, role });
  return result.data.inviteId;
}

export async function cancelInvite(orgId: string, inviteId: string): Promise<void> {
  const callable = httpsCallable(functions, 'cancelInvite');
  await callable({ orgId, inviteId });
}

export async function acceptInvite(orgId: string, inviteId: string): Promise<string> {
  const callable = httpsCallable<unknown, { orgId: string }>(functions, 'acceptInvite');
  const result = await callable({ orgId, inviteId });
  return result.data.orgId;
}

export async function listPendingInvites(orgId: string): Promise<PendingInvite[]> {
  const snap = await getDocs(query(orgPendingInvitesCol(orgId), orderBy('createdAt', 'asc')));
  return snap.docs.map((d) => {
    const raw = d.data() as Omit<PendingInvite, 'createdAt'> & {
      createdAt: number | { toMillis(): number };
    };
    return {
      ...raw,
      createdAt:
        typeof raw.createdAt === 'number' ? raw.createdAt : raw.createdAt.toMillis(),
    };
  });
}

export async function getInvite(
  orgId: string,
  inviteId: string,
): Promise<PendingInvite | null> {
  const snap = await getDoc(orgPendingInviteDoc(orgId, inviteId));
  if (!snap.exists()) return null;
  const raw = snap.data() as Omit<PendingInvite, 'createdAt'> & {
    createdAt: number | { toMillis(): number };
  };
  return {
    ...raw,
    createdAt:
      typeof raw.createdAt === 'number' ? raw.createdAt : raw.createdAt.toMillis(),
  };
}
